import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { Icon } from '../Icon/Icon.js';

import { IImagePickerImage } from './models/IImagePickerImage.js';
import { IImagePickerConfiguration, IImagePickerProps, IImagePickerState } from './models/index.js';
import Buttons from './styledComponents/Buttons.js';
import CoveringButton from './styledComponents/CoveringButton.js';
import PickerOverlay from './styledComponents/PickerOverlay.js';
import RefreshButton from './styledComponents/RefreshButton.js';
import SelectedImage from './styledComponents/SelectedImage.js';
import Thumbnail from './styledComponents/Thumbnail.js';
import ThumbnailImage from './styledComponents/ThumbnailImage.js';
import Thumbnails from './styledComponents/Thumbnails.js';
import ToggleButton from './styledComponents/ToggleButton.js';
import Wrapper from './styledComponents/Wrapper.js';
import { IMAGE_PICKER_CATEGORY_KEY, IMAGE_PICKER_COMPONENT_KEY } from './ImagePicker.meta.js';

export class ImagePicker extends ComponentBase<IImagePickerProps, IImagePickerState, IImagePickerConfiguration> {
	categoryKey = IMAGE_PICKER_CATEGORY_KEY;
	componentKey = IMAGE_PICKER_COMPONENT_KEY;
	showPickerOverlay = false;
	selected: IImagePickerImage | null = null;
	constructor(props: IImagePickerProps) {
		super(props);

		makeObservable(this, {
			showPickerOverlay: observable,
			selected: observable,
			initializeSelected: action,
			setSelected: action,
			handleSelect: action,
			togglePickerOverlay: action,
		});

		this.initializeSelected();
	}
	initializeSelected = () => {
		const { images = [] } = this.props;
		let { selected } = this.props;
		if (!selected && images.length > 0) {
			selected = images[0];
		}
		this.selected = selected !== undefined ? selected : null;
	};
	componentDidUpdate = (newProps: IImagePickerProps) => {
		if (!this.selected && newProps.selected) {
			this.setSelected(newProps.selected);
		}
	};
	setSelected = (selected: IImagePickerImage | null) => {
		this.selected = selected;
	};
	handleSelect = (image: IImagePickerImage) => {
		const { onSelect } = this.props;
		this.selected = image;
		this.togglePickerOverlay(false);
		if (onSelect) {
			onSelect(image);
		}
	};
	handleToggleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		this.togglePickerOverlay();
	};
	handleRefreshClick = () => {
		const { onRefresh } = this.props;
		if (onRefresh) {
			onRefresh();
		}
	};
	togglePickerOverlay = (mustShow?: boolean) => {
		this.showPickerOverlay = typeof mustShow === 'boolean' ? mustShow : !this.showPickerOverlay;
	};
	render() {
		const { className, images = [] } = this.props;

		return (
			<Wrapper className={className}>
				{!this.showPickerOverlay && (
					<CoveringButton onClick={this.handleToggleClick}>
						<SelectedImage src={this.selected && this.selected.src} />
						<Buttons>
							<ToggleButton>
								<Icon asset={'base.imagePicker.toggleDown'} />
							</ToggleButton>
						</Buttons>
					</CoveringButton>
				)}
				{this.showPickerOverlay && (
					<PickerOverlay>
						<Thumbnails>
							{images.map((image) => (
								<Thumbnail key={image.id} onClick={() => this.handleSelect(image)}>
									<ThumbnailImage src={image.src} />
								</Thumbnail>
							))}
						</Thumbnails>
						<Buttons>
							<ToggleButton onClick={this.handleToggleClick}>
								<Icon asset={'base.imagePicker.toggleUp'} />
							</ToggleButton>
							<RefreshButton onClick={this.handleRefreshClick}>
								<Icon asset={'base.imagePicker.refresh'} />
							</RefreshButton>
						</Buttons>
					</PickerOverlay>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(ImagePicker), IMAGE_PICKER_CATEGORY_KEY, IMAGE_PICKER_COMPONENT_KEY);
